import {gql, useQuery} from '@apollo/client';
import React from 'react';
import {LoadingSpinner, Paths, RelayConnection, SimpleDropdown, ErrorDisplay} from "../../../common";
import {SortOrder} from "../domain";
import {CaretDownIcon, CaretUpIcon, Icon, Pane} from "evergreen-ui";
import {RelayPagination, useRelayPagination} from "../../pagination";
import {ProgressPortrait, ProgressPortraitClientProfileListColumn, ProgressPortraitListFilterValue, ProgressPortraitSortBy} from "./domain";
import {ProgressPortraitPreview} from "./ProgressPortraitPreview";
import {ProgressPortraitListFilter, asQueryParam} from "./ProgressPortraitListFilter";
import {ProgressPortraitListFilterBadge} from "./ProgressPortraitListFilterBadge";
import {ProgressPortraitListAction} from "./progressPortraitListReducer";

export const PROGRESS_PORTRAITS_QUERY = gql`
    query progressPortraitsQuery(
        $first: Int = 50,
        $after: String,
        $params: ProgressPortraitQueryParamsInput!
    ) {
        progressPortraitsQuery(
            first: $first
            after: $after
            params: $params
        ) {
            totalElements
            pageInfo {
                hasPreviousPage
                hasNextPage
                startCursor
                endCursor
            }
            edges {
                node {
                    guid
                    sfCaseId
                    caseNumber
                    reportDate
                    hasProviderProgressPortrait
                    providerProgressPortraitFileName
                }
            }
        }
    }
`;

type ProgressPortraitQueryResponse = {
    progressPortraitsQuery: RelayConnection<ProgressPortrait> & { totalElements: number };
};

type Props = {
    dispatch: React.Dispatch<ProgressPortraitListAction>;
    pagination: any;
    filterValue: ProgressPortraitListFilterValue;
    selected: ProgressPortrait | null;
    sortBy: ProgressPortraitSortBy;
    pageSize: number;
}

export const ProgressPortraitList = ({
    dispatch,
    pagination,
    filterValue,
    selected,
    sortBy,
    pageSize
}: Props) => {
    const {loading, data, error} = useQuery<ProgressPortraitQueryResponse>(
        PROGRESS_PORTRAITS_QUERY,
        {
            variables: {
                ...pagination.toGraphQlParams(),
                params: {
                    ...asQueryParam(filterValue),
                    sortDir: sortBy.order,
                    sortBy: sortBy.column,
                },
            },
            fetchPolicy: "no-cache"
        }
    );

    const setSortByColumn = (newSortColumn: Paths<ProgressPortrait>) => {
        if (!['reportDate', 'caseNumber'].includes(newSortColumn)) {
            return;
        }

        let newSortOrder = SortOrder.DESC;
        if (newSortColumn === sortBy.column && sortBy.order === newSortOrder) {
            newSortOrder = SortOrder.ASC;
        }

        dispatch({type: 'SET_SORT_BY', sortBy: {column: newSortColumn, order: newSortOrder}});
    }

    let totalElements = 0;
    if (data?.progressPortraitsQuery?.pageInfo?.endCursor !== null) {
        totalElements = data?.progressPortraitsQuery?.totalElements ?? 0;
    }
    const {onPaginationClick} = useRelayPagination(dispatch, totalElements);

    if (error) {
        return <ErrorDisplay error={error} title="Error loading progress portraits" />;
    }

    return (
        <Pane className="h-full relative bg-white shadow-md flex flex-col">
            <div className="flex-1 min-h-0 pt-2 pb-5 bg-white shadow-md flex flex-col">
                <div className="flex-1 min-h-0 flex flex-col w-full relative">
                    <div className="flex justify-between items-start gap-4 px-5">
                        <div className="flex items-center">
                            <ProgressPortraitListFilterBadge filter={filterValue} dispatch={dispatch} className="pl-2"/>
                        </div>
                        <div className="flex items-center shrink-0">
                            <ProgressPortraitListFilter initFilterValue={filterValue} dispatch={dispatch}/>
                            <SimpleDropdown options={["10", "50", "100"]} selected={pageSize.toString()}
                                            minWidth={"2rem"}
                                            textColor={"gray-700"}
                                            setSelected={(newPageSize: string) => dispatch({
                                                type: "SET_PAGE_SIZE",
                                                pageSize: parseInt(newPageSize)
                                            })}/>
                            <RelayPagination pageInfo={data?.progressPortraitsQuery?.pageInfo} count={totalElements}
                                             onClick={onPaginationClick}/>
                        </div>
                    </div>

                    <div className="overflow-auto flex flex-col flex-grow min-h-0 mb-4 pt-4 pb-8 scrollbar-always">
                        <table className="table-fixed w-full">
                            <thead>
                            <tr className='text-left select-none text-sm text-gray-portal-table-title'>
                                {
                                    ProgressPortraitClientProfileListColumn.map((column, index) => (
                                        <th key={column.attr}
                                            className={'font-light' + (index === 0 ? ' pl-8' : '')}
                                            onClick={() => setSortByColumn(column.attr)}>
                                            <div className='flex items-center'>
                                                <div>
                                                    {column.name}
                                                </div>
                                                <div className='w-6 h-6 pl-2'>
                                                    {sortBy.column === column.attr && sortBy.order === SortOrder.ASC &&
                                                        <Icon className='align-middle' icon={CaretUpIcon}/>}
                                                    {sortBy.column === column.attr && sortBy.order === SortOrder.DESC &&
                                                        <Icon className='align-middle' icon={CaretDownIcon}/>}
                                                </div>
                                            </div>
                                        </th>
                                    ))
                                }
                            </tr>
                            </thead>
                            <tbody>
                            {
                                !loading && data?.progressPortraitsQuery.edges.map(({node}: {node: ProgressPortrait}) => (
                                    <ProgressPortraitPreview
                                        key={node.guid}
                                        portrait={node}
                                        isSelected={selected?.guid === node.guid}
                                        onClick={() => dispatch({type: 'SET_SELECTED', selected: node})}
                                    />
                                ))
                            }
                            </tbody>
                        </table>
                        {
                            loading &&
                            <div className="w-full h-full flex flex-grow items-center">
                                <LoadingSpinner spacing={20} height={"100%"}/>
                            </div>
                        }
                        {
                            totalElements === 0 && !loading && <div className="w-full h-full flex flex-grow items-center">
                                <div className="m-auto p-10">
                                    No progress portraits matched the searching criteria.
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </Pane>
    );
};
